"use client";

import { useEffect, useState } from "react";
import clsx from "clsx";
import { listCalculators } from "@/calculators/registry";
import { filterCalculatorsByAccess } from "@/calculators/calculatorAccess";
import { useLaunchCalculator } from "@/calculators/launch";
import { Button } from "@/components/ui/button/Button";
import { useModulePermissions } from "@/context/ModulePermissionsContext";
import { getPlanFeesSnapshot } from "@/services/api/agentic-v2/planFees";
import { getPlanFeaturesSnapshot } from "@/services/api/agentic-v2/planFeatures";
import { filterCalculatorPlans } from "@/calculators/planFees/planFilter";
import { countPlansInFeaturesSnapshot } from "@/calculators/planFeatures/snapshotMeta";
import {
  WORKBENCH_MUTED_TEXT_CLASS,
  WORKBENCH_SUBTLE_TEXT_CLASS,
} from "@/components/ui/workbench";

export type ClientCalculatorLinksProps = {
  faClientId: string;
  /** Full client tab panel vs compact card (legacy). */
  variant?: "tab" | "card";
};

function formatCompiledAt(iso: string): string {
  try {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

/** Launch client-scoped calculators with household pre-filled (calculators module only). */
export function ClientCalculatorLinks({
  faClientId,
  variant = "card",
}: ClientCalculatorLinksProps) {
  const { permissions, hasPermission, isLoading: permissionsLoading } = useModulePermissions();
  const launch = useLaunchCalculator();
  const calculators = filterCalculatorsByAccess(
    listCalculators().filter((c) => c.scope === "client"),
    permissions?.permissions,
  );

  const [planFeesMeta, setPlanFeesMeta] = useState<{
    version: number;
    compiledAt: string;
    planCount: number;
  } | null>(null);
  const [planFeesLoading, setPlanFeesLoading] = useState(false);

  const [planFeaturesMeta, setPlanFeaturesMeta] = useState<{
    version: number;
    compiledAt: string;
    planCount: number;
  } | null>(null);
  const [planFeaturesLoading, setPlanFeaturesLoading] = useState(false);

  const canUseCalculators = hasPermission("calculators");

  useEffect(() => {
    if (!canUseCalculators || !faClientId) {
      setPlanFeesMeta(null);
      return;
    }
    let cancelled = false;
    setPlanFeesLoading(true);
    getPlanFeesSnapshot(faClientId)
      .then((snap) => {
        if (cancelled) return;
        if (!snap) {
          setPlanFeesMeta(null);
          return;
        }
        const plans = filterCalculatorPlans(snap.plans ?? []);
        setPlanFeesMeta({
          version: snap.version,
          compiledAt: snap.compiledAt,
          planCount: plans.length,
        });
      })
      .catch(() => {
        if (!cancelled) setPlanFeesMeta(null);
      })
      .finally(() => {
        if (!cancelled) setPlanFeesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [canUseCalculators, faClientId]);

  useEffect(() => {
    if (!canUseCalculators || !faClientId) {
      setPlanFeaturesMeta(null);
      return;
    }
    let cancelled = false;
    setPlanFeaturesLoading(true);
    getPlanFeaturesSnapshot(faClientId)
      .then((snap) => {
        if (cancelled) return;
        if (!snap) {
          setPlanFeaturesMeta(null);
          return;
        }
        setPlanFeaturesMeta({
          version: snap.version,
          compiledAt: snap.compiledAt,
          planCount: countPlansInFeaturesSnapshot(snap),
        });
      })
      .catch(() => {
        if (!cancelled) setPlanFeaturesMeta(null);
      })
      .finally(() => {
        if (!cancelled) setPlanFeaturesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [canUseCalculators, faClientId]);

  if (permissionsLoading || !canUseCalculators || calculators.length === 0) {
    return null;
  }

  const hasPlanFees = calculators.some((c) => c.id === "plan-fees");
  const hasPlanFeatures = calculators.some((c) => c.id === "plan-features");

  const draftMetaLine = (
    meta: { version: number; compiledAt: string; planCount: number } | null,
    loading: boolean,
  ) =>
    loading
      ? "Loading draft…"
      : meta
        ? `Draft v${meta.version} · ${meta.planCount} plan${meta.planCount === 1 ? "" : "s"} · ${formatCompiledAt(meta.compiledAt)}`
        : "No draft yet";

  const planFeesCardMeta = hasPlanFees ? draftMetaLine(planFeesMeta, planFeesLoading) : null;
  const planFeaturesCardMeta = hasPlanFeatures
    ? draftMetaLine(planFeaturesMeta, planFeaturesLoading)
    : null;

  const tileMeta = (calculatorId: string): string | null => {
    if (calculatorId === "plan-fees") return planFeesCardMeta;
    if (calculatorId === "plan-features") return planFeaturesCardMeta;
    return null;
  };

  const statusLine = hasPlanFees
    ? planFeesLoading
      ? "Loading plan fees draft…"
      : planFeesMeta
        ? `Plan fees draft v${planFeesMeta.version} · ${planFeesMeta.planCount} plan${planFeesMeta.planCount === 1 ? "" : "s"} · updated ${formatCompiledAt(planFeesMeta.compiledAt)}`
        : "No plan fees draft yet — open Plan fees to create one."
    : "Open a calculator for this household.";

  if (variant === "tab") {
    return (
      <ul
        className="grid max-w-2xl gap-3 sm:grid-cols-2"
        data-testid="client-calculator-links"
      >
        {calculators.map((calc) => (
          <li key={calc.id}>
            <button
              type="button"
              className={clsx(
                "flex h-full w-full flex-col items-start gap-2 rounded-xl border p-4 text-left transition-colors",
                "border-slate-200 bg-white hover:bg-slate-50 dark:border-white/10 dark:bg-white/5 dark:hover:bg-white/[0.08]",
              )}
              onClick={() => launch({ calculatorId: calc.id, faClientId })}
              data-testid={`client-open-calculator-${calc.id}`}
            >
              <span className="text-sm font-medium text-slate-900 dark:text-slate-100">
                {calc.label}
              </span>
              <span className={clsx("text-xs", WORKBENCH_SUBTLE_TEXT_CLASS)}>
                {calc.description}
              </span>
              {tileMeta(calc.id) ? (
                <span className={clsx("text-xs tabular-nums", WORKBENCH_MUTED_TEXT_CLASS)}>
                  {tileMeta(calc.id)}
                </span>
              ) : null}
            </button>
          </li>
        ))}
      </ul>
    );
  }

  return (
    <section
      className="rounded-xl border border-slate-200 bg-white px-4 py-3 dark:border-white/10 dark:bg-white/5"
      data-testid="client-calculator-links"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className={clsx("text-xs font-medium uppercase tracking-wide", WORKBENCH_SUBTLE_TEXT_CLASS)}>
            Calculators
          </h2>
          <p className={clsx("mt-1 text-sm", WORKBENCH_MUTED_TEXT_CLASS)}>{statusLine}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {calculators.map((calc) => (
            <Button
              key={calc.id}
              color="zinc"
              size="sm"
              onClick={() => launch({ calculatorId: calc.id, faClientId })}
              data-testid={`client-open-calculator-${calc.id}`}
            >
              {calc.label}
            </Button>
          ))}
        </div>
      </div>
    </section>
  );
}
